import React from 'react';
import {
  FinanceResponseData,
  FinalFee,
  FinalFeeType,
  FinalProduct,
  FinalProductOrigin,
} from '@upstart/harmony/lib/legos/financingTypes';
import { ProductCategory } from '@upstart/harmony/lib/legos/penTypes';
import { parseCurrencyTruncated } from '@upstart/harmony/lib/form-utils';
import { ProductTableType } from '../ricFormTypes';
import {
  formatCurrency,
  getProductsTotal,
  parseNumberIfPresent,
} from '../ricFormUtils';
import { SectionItem, SubsectionItem, ProductTable } from '../Components';
import { FormTable } from '../Components/FormItems';
import Input, { PriceInput } from '../../../../components/PencilEditor/Input';

const REQUIRED_PRODUCT_FIELDS = ['paidTo', 'description'];

type FeeRow = { kind: 'fee'; data: FinalFee };
type ProductRow = { kind: 'product'; data: FinalProduct };
type TableRow = FeeRow | ProductRow;

const SchemaProductTable = ({
  formElement,
  responseData,
  fetchResponseWithUpdatedData,
  isSubSectionElement,
}: {
  formElement: ProductTableType;
  isSubSectionElement: boolean;
  responseData: FinanceResponseData;
  fetchResponseWithUpdatedData: (data: Partial<FinanceResponseData>) => void;
}) => {
  const { title, subtitle, columns, productCategory, displayTotalValue } =
    formElement;

  const products = (responseData?.finalizedProductList ?? []).filter(
    (p) =>
      productCategory === p.category && p.origin !== FinalProductOrigin.FEE,
  );

  const feeProducts = (responseData?.finalizedProductList ?? []).filter(
    (p) =>
      productCategory === p.category && p.origin === FinalProductOrigin.FEE,
  );
  const feeProductsValue = displayTotalValue
    ? formatCurrency(getProductsTotal(feeProducts))
    : null;

  const otherChargeFees =
    productCategory === ProductCategory.SERVICE_CONTRACT
      ? (responseData?.finalizedFeeList ?? []).filter(
          (f) => f.type === FinalFeeType.OTHER_CHARGES,
        )
      : [];

  const productsValue = displayTotalValue
    ? formatCurrency(
        getProductsTotal(products) +
          otherChargeFees.reduce((sum, f) => sum + (f.value || 0), 0),
      )
    : null;

  const filledProductCount = products.filter(
    (p) => p.value || p.description || p.paidTo,
  ).length;
  const visibleProducts = products.slice(
    0,
    Math.max(filledProductCount, products.length - otherChargeFees.length),
  );

  const updateFee = (fee: FinalFee, newData: Partial<FinalFee>) => {
    const newFeeList = [...(responseData?.finalizedFeeList ?? [])];
    const feeIndex = newFeeList.findIndex((f) => f === fee);
    if (feeIndex < 0) return;
    newFeeList[feeIndex] = { ...newFeeList[feeIndex], ...newData };
    fetchResponseWithUpdatedData({ finalizedFeeList: newFeeList });
  };

  const updateProduct = (
    product: FinalProduct,
    newData: Partial<FinalProduct>,
  ) => {
    const newProductList = [...(responseData?.finalizedProductList ?? [])];
    const productIndex = newProductList.findIndex((p) => p === product);
    if (productIndex < 0) return;
    newProductList[productIndex] = {
      ...newProductList[productIndex],
      ...newData,
    };
    fetchResponseWithUpdatedData({ finalizedProductList: newProductList });
  };

  const getFieldValue = (fieldName: string, val: string) => {
    if (['months', 'miles'].includes(fieldName)) {
      return parseNumberIfPresent(val);
    }
    return val;
  };

  const rows: TableRow[] = [
    ...otherChargeFees.map((fee): FeeRow => ({ kind: 'fee', data: fee })),
    ...visibleProducts.map(
      (product): ProductRow => ({ kind: 'product', data: product }),
    ),
  ];

  const buildDescriptions = (row: TableRow) =>
    columns.map((col) => {
      if (row.kind === 'fee') {
        if (col.fieldName === 'description') {
          return (
            <span key={col.fieldName} style={{ fontWeight: 'normal' }}>
              {row.data.description}
            </span>
          );
        }
        if (col.fieldName === 'paidTo') {
          return (
            <Input
              key={col.fieldName}
              required={!!row.data.value}
              value={row.data.paidTo}
              placeholder="N/A"
              onChange={(val: string) => updateFee(row.data, { paidTo: val })}
            />
          );
        }
        // product-only columns (e.g. months, miles) are not applicable to fees
        return <span key={col.fieldName} />;
      }
      return (
        <Input
          key={col.fieldName}
          required={
            REQUIRED_PRODUCT_FIELDS.includes(col.fieldName) && !!row.data.value
          }
          value={
            (row.data as unknown as Record<string, unknown>)[
              col.fieldName
            ] as string
          }
          placeholder="N/A"
          onChange={(val: string) =>
            updateProduct(row.data, {
              [col.fieldName]: getFieldValue(col.fieldName, val),
            })
          }
        />
      );
    });

  const buildValue = (row: TableRow) => (
    <PriceInput
      value={row.data.value}
      placeholder="N/A"
      onChange={(val) => {
        const parsed = parseCurrencyTruncated(val);
        if (row.kind === 'fee') {
          updateFee(row.data, { value: parsed });
        } else {
          updateProduct(row.data, { value: parsed });
        }
      }}
    />
  );

  const HeaderComponent = isSubSectionElement ? SubsectionItem : SectionItem;

  return (
    <>
      {feeProducts.length > 0 && (
        <>
          <HeaderComponent
            title={`${title} (Fees)`}
            subtitle={subtitle}
            value={feeProductsValue}
          />
          <div className="form-subsection">
            <ProductTable
              columns={columns}
              responseData={responseData}
              products={feeProducts}
              onChange={fetchResponseWithUpdatedData}
            />
          </div>
        </>
      )}
      {rows.length > 0 && (
        <>
          <HeaderComponent
            title={title}
            subtitle={subtitle}
            value={productsValue}
          />
          <div className="form-subsection">
            <FormTable
              columns={columns}
              rows={rows.map((row) => ({
                descriptions: buildDescriptions(row),
                value: buildValue(row),
              }))}
            />
          </div>
        </>
      )}
    </>
  );
};

export default SchemaProductTable;
